import { saveAs } from 'file-saver';
import { apiService } from '../../../services/apiService';
import { API_PATHS, getApproveStorefrontUrlPath, getRejectStorefrontUrlPath } from '../../../utils/apiPaths';
import { createCompleteGridFetcher } from '../../../utils/fetchCompleteGridList';
import type { StorefrontUrlReviewListRequest } from '../models/types';
import { mapStorefrontUrlSubmissionRow } from '../../storefront-urls/utils/utils';
import { buildStorefrontUrlReviewRequest } from '../utils/utils';

export async function fetchStorefrontUrlReviewList(payload: StorefrontUrlReviewListRequest) {
  const res: any = await apiService.post(API_PATHS.GET_ALL_STOREFRONT_URLS, payload);
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load URL submissions.');
  }

  const payloadData = res.data ?? {};
  const list = Array.isArray(payloadData)
    ? payloadData
    : Array.isArray(payloadData.content)
      ? payloadData.content
      : [];

  return {
    rows: list.map((item: Record<string, unknown>) => mapStorefrontUrlSubmissionRow(item)),
    totalElements: Number(payloadData.totalElements ?? list.length),
  };
}

export const fetchStorefrontUrlReviewGrid = createCompleteGridFetcher((
  options: Parameters<typeof buildStorefrontUrlReviewRequest>[0],
) => fetchStorefrontUrlReviewList(buildStorefrontUrlReviewRequest(options)));

export async function approveStorefrontUrl(id: number): Promise<void> {
  const res: any = await apiService.patch(getApproveStorefrontUrlPath(id));
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to approve URL.');
  }
}

export async function rejectStorefrontUrl(id: number, reason: string): Promise<void> {
  const res: any = await apiService.patch(getRejectStorefrontUrlPath(id), { reason: reason || undefined });
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to reject URL.');
  }
}

export interface BulkUploadRowError {
  row: number;
  merchantName: string;
  url: string;
  reason: string;
}

export interface BulkUploadResult {
  totalRows: number;
  created: number;
  skipped: number;
  failures: BulkUploadRowError[];
  message?: string;
}

/** Super-admin only — download the .xlsx template accepted by the bulk upload. */
export async function downloadStorefrontUrlTemplate(): Promise<void> {
  const blob = await apiService.get<Blob>(API_PATHS.STOREFRONT_URL_TEMPLATE, { responseType: 'blob' });
  saveAs(blob, 'storefront_urls_template.xlsx');
}

/** Super-admin only — upload an .xlsx (Merchant Name, URL) and bulk-create approved URLs. */
export async function bulkUploadStorefrontUrls(file: File): Promise<BulkUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res: any = await apiService.post(API_PATHS.BULK_UPLOAD_STOREFRONT_URLS, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!res?.success) {
    throw new Error(res?.message || 'Upload failed.');
  }
  return { ...(res.data as BulkUploadResult), message: res.message };
}
